import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ApiRunOfShowEntry } from '../api/runOfShow';
import { formatTimeOnly, formatTimezoneName } from './eventDate';

const MUSICADE_LOGO_SRC = '/Musicade-Logo.png';

const INK = { r: 17, g: 17, b: 17 };
const MUTED = { r: 113, g: 113, b: 122 }; // zinc-500
const BORDER = { r: 228, g: 228, b: 231 }; // zinc-200
const HEAD_FILL = { r: 244, g: 244, b: 245 }; // zinc-100
const ZEBRA_FILL = { r: 250, g: 250, b: 250 }; // zinc-50
const BLUE = { r: 37, g: 99, b: 235 }; // blue-600
const BLUE_FILL = { r: 239, g: 246, b: 255 }; // blue-50

const TABLE_RADIUS = 3;
const PILL_FONT_SIZE = 7;
const PILL_HEIGHT = 4.6;
const PILL_PADDING_X = 2.2;
const PILL_GAP_X = 1.5;
const PILL_GAP_Y = 1.3;
const ASSIGNED_COL_WIDTH = 40;
const ASSIGNED_CELL_PAD = 4;
const ASSIGNED_LEFT_OFFSET = 2.5;
const ASSIGNED_CONTENT_WIDTH = ASSIGNED_COL_WIDTH - ASSIGNED_CELL_PAD * 2 - ASSIGNED_LEFT_OFFSET;
const TIME_COL_WIDTH = 30;
const TIME_CELL_PAD_X = 4;
const TIME_CELL_PAD_TOP = 3.5;
const TIME_CELL_PAD_BOTTOM = 3.5;
const TIME_FONT_SIZE = 8.5;
const TIME_LINE_HEIGHT = 3.5;
const TIME_TZ_GAP = 1;
const TZ_FONT_SIZE = 6.3;
const TZ_LINE_HEIGHT = 2.6;
const TIME_CONTENT_WIDTH = TIME_COL_WIDTH - TIME_CELL_PAD_X * 2;

const assigneeNames = (entry: ApiRunOfShowEntry): string[] => {
  if (!entry.assignees || entry.assignees.length === 0) return ['UNASSIGNED'];
  return entry.assignees.map((a) =>
    (a.full_name || a.username || a.email || 'Unknown').toUpperCase(),
  );
};

const loadImage = (src: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

// Draws the logo onto a canvas and re-fills every opaque pixel black, so the
// footer mark stays black regardless of the source asset's own colors.
const loadLogoAsBlackSilhouette = async (src: string): Promise<string | null> => {
  const img = await loadImage(src);
  if (!img) return null;
  const size = 96;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, size, size);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, size, size);
  return canvas.toDataURL('image/png');
};

// Wraps chip labels into rows that fit within maxWidth, given the doc's
// current font — matches the font set right before calling this.
const wrapChips = (doc: jsPDF, labels: string[], maxWidth: number): string[][] => {
  const lines: string[][] = [];
  let currentLine: string[] = [];
  let currentWidth = 0;

  for (const label of labels) {
    const chipWidth = doc.getTextWidth(label) + PILL_PADDING_X * 2;
    const extra = currentLine.length > 0 ? PILL_GAP_X : 0;
    if (currentLine.length > 0 && currentWidth + extra + chipWidth > maxWidth) {
      lines.push(currentLine);
      currentLine = [label];
      currentWidth = chipWidth;
    } else {
      currentLine.push(label);
      currentWidth += extra + chipWidth;
    }
  }
  if (currentLine.length > 0) lines.push(currentLine);
  return lines;
};

export async function exportRunOfShowPdf(params: {
  projectTitle: string;
  projectDateRange?: string;
  entries: ApiRunOfShowEntry[];
}): Promise<void> {
  const { projectTitle, projectDateRange, entries } = params;
  const blackLogo = await loadLogoAsBlackSilhouette(MUSICADE_LOGO_SRC);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(INK.r, INK.g, INK.b);
  doc.text(projectTitle, marginX, 19);

  if (projectDateRange) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(MUTED.r, MUTED.g, MUTED.b);
    doc.text(projectDateRange, marginX, 25);
  }

  doc.setDrawColor(BORDER.r, BORDER.g, BORDER.b);
  doc.line(marginX, 31, pageWidth - marginX, 31);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(INK.r, INK.g, INK.b);
  doc.text('Event Day Schedule', marginX, 43);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(MUTED.r, MUTED.g, MUTED.b);
  doc.text('Official Run of Show document for production crew and artists.', marginX, 50);

  const tableStartY = 58;
  const namesByRow = entries.map((e) => assigneeNames(e));
  const wrapByRow = new Map<number, string[][]>();
  const tzByRow = entries.map((e) =>
    e.iana_timezone ? formatTimezoneName(e.start_datetime, e.iana_timezone) : '',
  );

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(TZ_FONT_SIZE);
  const tzLinesByRow = tzByRow.map((tz) =>
    tz ? (doc.splitTextToSize(tz, TIME_CONTENT_WIDTH) as string[]) : [],
  );

  const rows = entries.map((e, i) => [
    formatTimeOnly(e.start_datetime, e.iana_timezone, true),
    e.title,
    e.notes || e.location || '—',
    namesByRow[i].join(', '),
  ]);

  let tableBottom = tableStartY;

  autoTable(doc, {
    startY: tableStartY,
    head: [['TIME', 'ITEM', 'DETAILS', 'ASSIGNED TO']],
    body: rows,
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 8.5,
      cellPadding: { top: 3.5, bottom: 3.5, left: 4, right: 4 },
      lineColor: [BORDER.r, BORDER.g, BORDER.b],
      lineWidth: { top: 0, left: 0, right: 0, bottom: 0.1 },
      textColor: [INK.r, INK.g, INK.b],
    },
    headStyles: {
      fillColor: [HEAD_FILL.r, HEAD_FILL.g, HEAD_FILL.b],
      textColor: [MUTED.r, MUTED.g, MUTED.b],
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'left',
      lineWidth: { top: 0, left: 0, right: 0, bottom: 0.3 },
    },
    alternateRowStyles: {
      fillColor: [ZEBRA_FILL.r, ZEBRA_FILL.g, ZEBRA_FILL.b],
    },
    columnStyles: {
      0: { cellWidth: TIME_COL_WIDTH, fontStyle: 'bold', textColor: [BLUE.r, BLUE.g, BLUE.b] },
      1: { cellWidth: 34, fontStyle: 'bold' },
      2: { cellWidth: 'auto', textColor: [MUTED.r, MUTED.g, MUTED.b] },
      3: { cellWidth: ASSIGNED_COL_WIDTH },
    },
    margin: { left: marginX, right: marginX },
    rowPageBreak: 'avoid',
    didParseCell: (data) => {
      if (data.section !== 'body') return;

      if (data.column.index === 0) {
        const tzLines = tzLinesByRow[data.row.index];
        if (tzLines.length > 0) {
          data.cell.styles.minCellHeight =
            TIME_CELL_PAD_TOP +
            TIME_LINE_HEIGHT +
            TIME_TZ_GAP +
            tzLines.length * TZ_LINE_HEIGHT +
            TIME_CELL_PAD_BOTTOM;
          data.cell.text = [];
        }
        return;
      }

      if (data.column.index !== 3) return;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(PILL_FONT_SIZE);
      const wrapped = wrapChips(doc, namesByRow[data.row.index], ASSIGNED_CONTENT_WIDTH);
      wrapByRow.set(data.row.index, wrapped);

      const neededHeight =
        wrapped.length * PILL_HEIGHT +
        (wrapped.length - 1) * PILL_GAP_Y +
        ASSIGNED_CELL_PAD * 2 -
        1.5;
      data.cell.styles.minCellHeight = neededHeight;
      data.cell.text = [];
    },
    didDrawCell: (data) => {
      tableBottom = Math.max(tableBottom, data.cell.y + data.cell.height);
      if (data.section !== 'body') return;

      if (data.column.index === 0) {
        const tzLines = tzLinesByRow[data.row.index];
        if (tzLines.length === 0) return;

        const textX = data.cell.x + TIME_CELL_PAD_X;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(TIME_FONT_SIZE);
        doc.setTextColor(BLUE.r, BLUE.g, BLUE.b);
        const timeY = data.cell.y + TIME_CELL_PAD_TOP + TIME_FONT_SIZE * 0.36;
        doc.text(rows[data.row.index][0] as string, textX, timeY);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(TZ_FONT_SIZE);
        doc.setTextColor(MUTED.r, MUTED.g, MUTED.b);
        const tzY = timeY + TZ_FONT_SIZE * 0.36 + TIME_TZ_GAP + TZ_LINE_HEIGHT;
        doc.text(tzLines, textX, tzY);
        return;
      }

      if (data.column.index !== 3) return;

      const wrapped = wrapByRow.get(data.row.index) ?? [];
      const totalHeight = wrapped.length * PILL_HEIGHT + (wrapped.length - 1) * PILL_GAP_Y;
      let pillY = data.cell.y + (data.cell.height - totalHeight) / 2;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(PILL_FONT_SIZE);

      for (const line of wrapped) {
        let pillX = data.cell.x + ASSIGNED_LEFT_OFFSET;
        for (const label of line) {
          const textWidth = doc.getTextWidth(label);
          const pillWidth = textWidth + PILL_PADDING_X * 2;

          doc.setFillColor(BLUE_FILL.r, BLUE_FILL.g, BLUE_FILL.b);
          doc.setDrawColor(BLUE.r, BLUE.g, BLUE.b);
          doc.setLineWidth(0.25);
          doc.roundedRect(pillX, pillY, pillWidth, PILL_HEIGHT, 2.3, 2.3, 'FD');

          doc.setTextColor(BLUE.r, BLUE.g, BLUE.b);
          doc.text(label, pillX + PILL_PADDING_X, pillY + PILL_HEIGHT / 2 + 1);

          pillX += pillWidth + PILL_GAP_X;
        }
        pillY += PILL_HEIGHT + PILL_GAP_Y;
      }
    },
  });

  const tableWidth = pageWidth - marginX * 2;
  doc.setDrawColor(BORDER.r, BORDER.g, BORDER.b);
  doc.setLineWidth(0.35);
  doc.roundedRect(
    marginX,
    tableStartY,
    tableWidth,
    tableBottom - tableStartY,
    TABLE_RADIUS,
    TABLE_RADIUS,
    'S',
  );

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerY = pageHeight - 12;

    doc.setDrawColor(BORDER.r, BORDER.g, BORDER.b);
    doc.line(marginX, footerY - 6, pageWidth - marginX, footerY - 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(MUTED.r, MUTED.g, MUTED.b);
    doc.text('Generated by', marginX, footerY);
    const generatedByWidth = doc.getTextWidth('Generated by');

    let musicadeX = marginX + generatedByWidth + 2.5;
    if (blackLogo) {
      const logoSize = 4;
      doc.addImage(blackLogo, 'PNG', musicadeX, footerY - logoSize + 0.8, logoSize, logoSize);
      musicadeX += logoSize + 2;
    }

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(INK.r, INK.g, INK.b);
    doc.text('MUSICADE', musicadeX, footerY);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(MUTED.r, MUTED.g, MUTED.b);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, footerY, { align: 'right' });
  }

  const fileName = `${projectTitle.toLowerCase().replace(/\s+/g, '_')}_run_of_show_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}
